import React, { useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import './styles/tailwind.css';
import './styles/global.css';
import './styles/components.css';
import './styles/layout.css';
import './styles/admin.css';
import './styles/landing.css';
import { useAuthStore } from './store/auth';
import { fetchMe, getAccessToken, setUnauthorizedHandler } from './lib/api';
import { connectSocket, disconnectSocket } from './lib/ws';
import { LandingPage } from './pages/Landing';
import { TermsPage, PrivacyPage, RefundPage } from './pages/Legal';
import { PlayerPage } from './pages/Player';
import { LoginPage } from './pages/auth/Login';
import { SignupPage } from './pages/auth/Signup';
import { RegisterPage } from './pages/auth/Register';
import { ForgotPasswordPage, ResetPasswordPage, VerifyEmailPage } from './pages/auth/Password';
import { DashboardPage } from './pages/tenant/Dashboard';
import { StreamsPage } from './pages/tenant/Streams';
import { StreamDetailPage } from './pages/tenant/StreamDetail';
import { CreateStreamPage } from './pages/tenant/CreateStream';
import { DestinationsPage } from './pages/tenant/Destinations';
import { RecordingsPage } from './pages/tenant/Recordings';
import { CreateDestinationPage } from './pages/tenant/CreateDestination';
import { AnalyticsPage } from './pages/tenant/Analytics';
import { BillingPage } from './pages/tenant/Billing';
import { TransactionsPage } from './pages/tenant/Transactions';
import { ProfilePage } from './pages/tenant/Settings/Profile';
import { SecurityPage } from './pages/tenant/Settings/Security';
import { PreferencesPage } from './pages/tenant/Settings/Preferences';
import { AdminDashboardPage } from './pages/admin/Dashboard';
import { AdminMonitoringPage } from './pages/admin/Monitoring';
import { AdminReportsPage } from './pages/admin/Reports';
import { AdminTransactionsPage } from './pages/admin/Transactions';
import { AdminSubscriptionsPage } from './pages/admin/Subscriptions';
import { AdminStreamsPage } from './pages/admin/Streams';
import { AdminAuditLogPage } from './pages/admin/AuditLog';
import { AdminTenantsPage } from './pages/admin/Tenants';
import { AdminTenantDetailPage } from './pages/admin/TenantDetail';
import { AdminRecordingsPage } from './pages/admin/Recordings';
import { AdminPlansPage } from './pages/admin/Plans';
import { AdminSettingsPage } from './pages/admin/Settings';
import { AdminProfilePage } from './pages/admin/Profile';
import { AdminUsersPage } from './pages/admin/AdminUsers';
import AdminChatListPage from './pages/admin/Chat/ChatList';
import AdminChatConversationPage from './pages/admin/Chat/ChatConversation';
import TenantChatListPage from './pages/tenant/Chat/ChatList';
import TenantChatConversationPage from './pages/tenant/Chat/ChatConversation';
import { ToastRenderer } from './components/ToastRenderer';
import { useUiStore } from './store/ui';
import { useBrandingStore } from './store/branding';

// Apply the persisted theme before first paint so there is no light/dark flash.
document.documentElement.dataset.theme = useUiStore.getState().theme;

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const auth = useAuthStore();
  if (!auth.user && !auth.loading) {
    return <Navigate to="/login" replace />;
  }
  if (auth.loading) {
    return (
      <div className="layout-auth" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="text-muted">Loading…</div>
      </div>
    );
  }
  return <>{children}</>;
}

function PublicOnly({ children }: { children: React.ReactNode }) {
  const auth = useAuthStore();
  if (auth.user && !auth.loading) {
    return <Navigate to={auth.user.role === 'SUPER_ADMIN' ? '/admin' : '/dashboard'} replace />;
  }
  return <>{children}</>;
}

function AuthInitializer() {
  const setLoading = useAuthStore((s) => s.setLoading);
  const setSession = useAuthStore((s) => s.setSession);

  useEffect(() => {
    setLoading(true);
    void useBrandingStore.getState().load();

    // Server-side logout (session revoked, tenant suspended): drop the local
    // session so ProtectedRoute bounces the user to /login instead of leaving
    // them on a page whose every request now401s.
    setUnauthorizedHandler((message) => {
      const state = useAuthStore.getState();
      if (!state.user) return; // nothing to log out (cold load already lands on /login)
      state.clear();
      disconnectSocket();
      useUiStore.getState().toast('warning', 'Signed out', message);
    });

    void (async () => {
      const me = await fetchMe();
      if (me) {
        // /auth/me does not return an accessToken; the token was already
        // stored by the refresh flow inside lib/api.ts.
        const token = getAccessToken();
        if (token) {
          setSession(me.user, me.tenant ?? null, token);
          // `tenant.suspended` is handled inside lib/ws.ts (registered on the
          // socket itself), so no per-page handler is needed here.
          connectSocket();
        }
      }
      setLoading(false);
    })();

    return () => setUnauthorizedHandler(null);
  }, [setLoading, setSession]);

  return null;
}

function App() {
  const auth = useAuthStore();

  return (
    <BrowserRouter>
      <AuthInitializer />
      <ToastRenderer />
      <Routes>
        <Route path="/player/:id" element={<PlayerPage />} />
        <Route path="/" element={<LandingPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/refund" element={<RefundPage />} />
        <Route path="/login" element={<PublicOnly><LoginPage /></PublicOnly>} />
        <Route path="/register" element={<PublicOnly><RegisterPage /></PublicOnly>} />
        <Route path="/signup" element={<PublicOnly><SignupPage /></PublicOnly>} />
        <Route path="/forgot-password" element={<PublicOnly><ForgotPasswordPage /></PublicOnly>} />
        <Route path="/reset-password" element={<PublicOnly><ResetPasswordPage /></PublicOnly>} />
        <Route path="/verify-email" element={<PublicOnly><VerifyEmailPage /></PublicOnly>} />
        <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
        <Route path="/streams" element={<ProtectedRoute><StreamsPage /></ProtectedRoute>} />
        <Route path="/recordings" element={<ProtectedRoute><RecordingsPage /></ProtectedRoute>} />
        <Route path="/streams/create" element={<ProtectedRoute><CreateStreamPage /></ProtectedRoute>} />
        <Route path="/streams/:id" element={<ProtectedRoute><StreamDetailPage /></ProtectedRoute>} />
        <Route path="/streams/:id/destinations" element={<ProtectedRoute><DestinationsPage /></ProtectedRoute>} />
        <Route path="/streams/:id/destinations/create" element={<ProtectedRoute><CreateDestinationPage /></ProtectedRoute>} />
        <Route path="/streams/:id/destinations/:destinationId/edit" element={<ProtectedRoute><CreateDestinationPage /></ProtectedRoute>} />
        <Route path="/analytics" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
        <Route path="/billing" element={<ProtectedRoute><BillingPage /></ProtectedRoute>} />
        <Route path="/transactions" element={<ProtectedRoute><TransactionsPage /></ProtectedRoute>} />
        <Route path="/settings/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
        <Route path="/settings/security" element={<ProtectedRoute><SecurityPage /></ProtectedRoute>} />
        <Route path="/settings/preferences" element={<ProtectedRoute><PreferencesPage /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute><AdminDashboardPage /></ProtectedRoute>} />
        <Route path="/admin/monitoring" element={<ProtectedRoute><AdminMonitoringPage /></ProtectedRoute>} />
        <Route path="/admin/reports" element={<ProtectedRoute><AdminReportsPage /></ProtectedRoute>} />
        <Route path="/admin/reports/transactions" element={<ProtectedRoute><AdminTransactionsPage /></ProtectedRoute>} />
        <Route path="/admin/reports/subscriptions" element={<ProtectedRoute><AdminSubscriptionsPage /></ProtectedRoute>} />
        <Route path="/admin/reports/streams" element={<ProtectedRoute><AdminStreamsPage /></ProtectedRoute>} />
        <Route path="/admin/reports/audit-log" element={<ProtectedRoute><AdminAuditLogPage /></ProtectedRoute>} />
        <Route path="/admin/tenants" element={<ProtectedRoute><AdminTenantsPage /></ProtectedRoute>} />
        <Route path="/admin/tenants/:id" element={<ProtectedRoute><AdminTenantDetailPage /></ProtectedRoute>} />
        <Route path="/admin/recordings" element={<ProtectedRoute><AdminRecordingsPage /></ProtectedRoute>} />
        <Route path="/admin/plans" element={<ProtectedRoute><AdminPlansPage /></ProtectedRoute>} />
        <Route path="/admin/settings" element={<ProtectedRoute><AdminSettingsPage /></ProtectedRoute>} />
        <Route path="/admin/users" element={<ProtectedRoute><AdminUsersPage /></ProtectedRoute>} />
<Route path="/admin/profile" element={<ProtectedRoute><AdminProfilePage /></ProtectedRoute>} />
        <Route path="/admin/chat" element={<ProtectedRoute><AdminChatListPage /></ProtectedRoute>} />
        <Route path="/admin/chat/:id" element={<ProtectedRoute><AdminChatConversationPage /></ProtectedRoute>} />
        <Route path="/chat" element={<ProtectedRoute><TenantChatListPage /></ProtectedRoute>} />
        <Route path="/chat/:id" element={<ProtectedRoute><TenantChatConversationPage /></ProtectedRoute>} />
        <Route path="*" element={<Navigate to={auth.user?.role === 'SUPER_ADMIN' ? '/admin' : '/dashboard'} replace />} />
      </Routes>
    </BrowserRouter>
  );
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
